import * as Lambda from '../../services/LambdaApi';
import { paginationWrapper } from '../../utils/paginationWrapper';

const fetchClassifiedCategories = async ({ facilityId }) => {
    const apiPath = `/s2l/${facilityId}/getServiceCategories`;

    const params = {
        queryStringParameters: { showActiveCategories: true },
    };
    const response = await Lambda.Get(apiPath, params, 'lambda');

    return response;
};


const fetchAllServicesInCategory = async ({
    facilityId,
    category,
    pageNo,
    pageSize,
    showActiveCategories,
    bookableOnly,
    residentId
}) => {
    const apiPath = `/s2l/${facilityId}/listServices`;
    const params =
    {
        Filter: {
            category:  category, 
            active: showActiveCategories,
            bookable: bookableOnly,
            ...(residentId && { residentId })
        },
        pagingSorting: {
            page_no: pageNo,
            page_size: pageSize,
            sort: [{ name: "desc" }]
        }
    };    
    const response = await Lambda.Post(apiPath, params, 'lambda');
    return response;
};

const listServices = async ({
    facilityId,
    pagingSorting,
}: {
    facilityId: string;
    pagingSorting: {
        page_size: number;
        page_no: number;
    };
}) => {
    const apiPath = `/s2l/${facilityId}/listServices`;
    const params = {
        Filter: {
            active: true,
        },
        pagingSorting,
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content?.Result) {
        throw new Error(response?.Result?.Error || 'Error fetching services');
    }
    return response.Result.Content as { Result: Array<{ _id: string; name?: string }>; TotRecords: number; };
};

const fetchAllServices = async (facilityId: string) => {
    if (!facilityId) {
        throw new Error('Facility ID is required');
    }

    return paginationWrapper({
        apiCall: listServices,
        params: {
            facilityId,
        },
    }) as Promise<Array<{ _id: string; name?: string }>>;
};

const fetchServiceImage = async ({ serviceId, Facility, fileName }) => {
    const apiPath = `/s2l/${Facility}/service-image`;
    const params = {
        queryStringParameters: { serviceId, Facility, fileName },
    };
    const response = await Lambda.Get(apiPath, params, 'lambda');
    if (!response || !response.Result || !response.Result.Content) {
        throw new Error('Error fetching service image');
    }
    return response.Result.Content;
};

const getServiceTypeConfig = async (FacilityId: string, withLogo?: boolean): Promise<any> => {
    try {
        let params;
        if (withLogo) {
            params = { _id: FacilityId, withLogo };
        } else {
            params = { _id: FacilityId };
        }
        const apiPath = `/s2l/${FacilityId}/service-type-config`;
        const res = await Lambda.Post(apiPath, params, 'lambda');
        return res;
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Failed to fetch service type config');
    }
};

const fetchOneServiceType = async (serviceId: string, Facility: string, residentId?: string): Promise<any> => {
    try {
        const apiPath = `/s2l/${Facility}/getOneService`;
        const params = { _id: serviceId, Facility, ...(residentId && { residentId }) };
        const res = await Lambda.Get(apiPath, { queryStringParameters: params }, 'lambda');
        if (!res || !res.Result || !res.Result.Content) {
            throw new Error('Error fetching service type');
        }
        return res;
    } catch (error) {
        console.log('error in fetchOne  serviceTypes', error);
        throw new Error(error instanceof Error ? error.message : 'Failed to fetch one service');
    }
};

/** Dimensions-of-Wellness category → hex color, as configured on the backend. */
const listDowMapping = async (facilityId: string): Promise<Record<string, string>> => {
    const apiPath = `/s2l/${facilityId}/listDowMapping`;
    const response = await Lambda.Get(apiPath, {}, 'lambda');
    const colorMapping = response?.Result?.Content?.colorMapping;
    if (!colorMapping) {
        throw new Error(response?.Result?.Error || 'Error fetching DOW mapping');
    }
    return colorMapping;
};

/** Category name → Font Awesome class names (e.g. "fa-light fa-headset"). */
const listServiceConfigurations = async (facilityId: string): Promise<Record<string, string>> => {
    if (!facilityId) {
        throw new Error('Facility ID is required');
    }
    const apiPath = `/s2l/${facilityId}/listServiceConfigurations`;
    const params = {
        queryStringParameters: { withLogo: true },
    };
    const response = await Lambda.Get(apiPath, params, 'lambda');
    const iconMap = response?.Result?.Content;
    if (!iconMap || typeof iconMap !== 'object' || Array.isArray(iconMap)) {
        throw new Error(response?.Result?.Error || 'Error fetching service configuration icons');
    }
    return iconMap as Record<string, string>;
};

export { fetchClassifiedCategories, fetchAllServicesInCategory, fetchServiceImage, getServiceTypeConfig , fetchOneServiceType, fetchAllServices, listDowMapping, listServiceConfigurations };
